import keyBinder from 'shared/lib/keyBinder';
import ConfirmActions from 'flux/actions/ConfirmActions';
import ModalActions from 'flux/actions/ModalActions';
import NewGlobalElementModalActions from 'flux/actions/NewGlobalElementModalActions';
import CuesheetCellActions from 'flux/actions/cuesheet/CuesheetCellActions';
import CuesheetRowActions from 'flux/actions/cuesheet/CuesheetRowActions';
import CuesheetToolbarActions from 'flux/actions/cuesheet/CuesheetToolbarActions';
import CuesheetColumnActions from 'flux/actions/cuesheet/CuesheetColumnActions';
import CuesheetCursorActions from 'flux/actions/cuesheet/CuesheetCursorActions';
import OnlineIndicatorActions from 'flux/actions/OnlineIndicatorActions';
import CuesheetTransientCellActions from 'flux/actions/cuesheet/CuesheetTransientCellActions';
import EventShowListActions from 'flux/actions/EventShowListActions';
import SearchActions from 'flux/actions/SearchActions';
import RTEActions from 'flux/actions/RTEActions';
import ActivityAuditListActions from 'flux/actions/ActivityAuditListActions';
import {
  InlineStyleHotkeyBindings,
  KeyScopes,
} from 'flux/stores/cuesheet/CuesheetConstants';
import KeyBindingActions from 'flux/actions/KeyBindingActions';
import isMobile from 'shared/lib/mobileCheck';
import role from 'shared/lib/role';
import UIRegions from 'shared/constants/UIRegions';
import { DisplayTypes } from 'shared/constants/FlowConstants';
import MessageConstants from 'shared/constants/Messages';
import { Map } from 'immutable';
import {
  isFeatured,
  isGlobalElementManager,
} from 'main/components/show/cuesheet/cuesheetViewHelper';

/**
 * BEGIN - key setup bindings
 */

let keysBound = false;

/**
 * Universal bindings
 *
 * These may be used even in mobile cases...some folks plug USB/bluetooth keyboards into their mobile devices
 */
let bindings = [
  ['space', '_handleSpaceKeyPress'],
  ['option+space, ctrl+space', '_handleCtrlSpaceKeyPress'],
  ['shift+space', '_handleShiftSpaceKeyPress'],
  ['alt+t', '_handleTrackSelectedKeyPress'],
  ['`', '_handleToggleRowSelectionKeyPress'],
  ['command+f, ctrl+f', '_toggleSearchVisibilityKeyPress'],
  [
    'shift+command+backspace, shift+ctrl+backspace, shift+alt+backspace',
    '_handleDeleteRowKeyPress',
  ],
  ['command+l, ctrl+l, alt+l, shift+command+l', '_handleToggleRowLockKeyPress'],
  ['command+c, ctrl+c', '_handleCopyCellsKeyPress'],
  ['command+x, ctrl+x', '_handleCutCellsKeyPress'],
  ['command+v, ctrl+v', '_handlePasteCellsKeyPress'],
  ['command+d, ctrl+d', '_handleDuplicateRowKeyPress'],
  ['command+\\, ctrl+\\', '_handleClearFormatCellKeyPress'],
  ['command+;, ctrl+;, alt+;, option+;', '_handleConvertToTitleRowKeyPress'],
  ['command+up, ctrl+up', '_handleMoveRowAboveKeyPress'],
  ['command+down, ctrl+down', '_handleMoveRowBelowKeyPress'],
  ['shift+command+up, shift+ctrl+up', '_handleAddRowAboveKeyPress'],
  ['shift+command+down, shift+ctrl+down', '_handleAddRowBelowKeyPress'],
  ['shift+command+right, shift+ctrl+right', '_handleMakeColumnGrowKeyPress'],
  ['shift+command+left, shift+ctrl+left', '_handleMakeColumnShrinkKeyPress'],
  ['command+alt+up, ctrl+alt+up', '_handleAddTitleRowAboveKeyPress'],
  ['command+alt+down, ctrl+alt+down', '_handleAddTitleRowBelowKeyPress'],
  ['shift+command+c, shift+ctrl+c', '_handleCopyRowKeyPress'],
  ['shift+command+x, shift+ctrl+x', '_handleCutRowKeyPress'],
  ['shift+command+v, shift+ctrl+v', '_handlePasteRowBelowKeyPress'],
  ['shift+command+g, shift+ctrl+g', '_handleToggleRowHighlightKeyPress'],
  [
    'shift+command+h, shift+ctrl+h',
    '_handleToggleRowPersonalHighlightKeyPress',
  ],
  ['alt+g', '_handleInsertGlobalElementsKeyPress'],
  ['alt+s', '_handleSaveCurrentGlobalElementKeyPress'],
  ['alt+r', '_handleRetrieveLatestGlobalElementVersionKeyPress'],
  ['shift+alt+s', '_handleCreateGlobalElementKeyPress'],
  [
    'shift+command+s, shift+ctrl+s',
    '_handlePublishGlobalElementChangesEverywhereKeyPress',
  ],
  ['command+s, ctrl+s', '_handleSaveShowKeyPress'],
  ['alt+k', '_handleDisplayKeyboardShortcutsModalKeyPress'],
  ['command+p, ctrl+p', '_handleDisplayAdvancedPdfModalKeyPress'],
  ['command+right, ctrl+right', '_handleMoveColumnRightKeyPress'],
  ['command+left, ctrl+left', '_handleMoveColumnLeftKeyPress'],
  ['command+a, ctrl+a', '_handleSelectAllRows'],
  ['command+b, ctrl+b', '_handleBoldTextKeyPress'],
  ['command+i, ctrl+i', '_handleItalicTextKeyPress'],
  [
    "shift+command+', shift+ctrl+', shift+alt+', shift+option+'",
    '_handleCellTextColorKeyPress',
  ],
  [
    'shift+command+;, shift+ctrl+;, shift+alt+;, shift+option+;',
    '_handleCellBgColorKeyPress',
  ],
  ['shift+command+=, shift+ctrl+=, shift+alt+=', '_handleCellTextGrowKeyPress'],
  [
    'shift+command+-, shift+ctrl+-, shift+option+-',
    '_handleCellTextShrinkKeyPress',
  ],
  ['command+1, ctrl+1, alt+1', '_handleCustomCellFormatOneKeyPress'],
  ['command+2, ctrl+2, alt+2', '_handleCustomCellFormatTwoKeyPress'],
  ['command+3, ctrl+3, alt+3', '_handleCustomCellFormatThreeKeyPress'],
  ['command+4, ctrl+4, alt+4', '_handleCustomCellFormatFourKeyPress'],
  ['command+5, ctrl+5, alt+5', '_handleCustomCellFormatFiveKeyPress'],
  ['command+6, ctrl+6, alt+6', '_handleCustomCellFormatSixKeyPress'],
  ['command+7, ctrl+7, alt+7', '_handleCustomCellFormatSevenKeyPress'],
  ['command+8, ctrl+8, alt+8', '_handleCustomCellFormatEightKeyPress'],
  ['command+9, ctrl+9, alt+9', '_handleCustomCellFormatNineKeyPress'],
  ['command+0, ctrl+0, alt+0', '_handleCustomCellFormatZeroKeyPress'],
  ['command+e, ctrl+e, alt+e', '_handleHideColumnKeyPress'],
  ['shift+command+e, shift+ctrl+e', '_handleShowAllColumnKeyPress'],
  ['command+z, ctrl+z', '_handleShowUndoForCellPress'],
  ['command+/, ctrl+/', '_handleToggleActivityFeedKeyPress'],
  ['command+g, ctrl+g', '_handleTimeStampKeyPress'],
];

const editModeBindings = InlineStyleHotkeyBindings.map((binding) =>
  binding.concat(KeyScopes.CellEditing),
);

/**
 * These key bindings will set a cell to edit mode when triggered
 */
const alphabeticCharacters = 'abcdefghijklmnopqrstuvwxyz'
  .split('')
  .reduce((prev, curr) => {
    return prev.concat(curr, `shift+${curr}`);
  }, []);

// This set of bindings does not include "`" which is used for row selection
// commas are not unbound properly with keymaster so we exclude them
const editActivationSpecialCharacters = "1234567890-=[];.\\/'"
  .split('')
  .reduce((prev, curr) => {
    return prev.concat(curr, `shift+${curr}`);
  }, []);

const editActivationBindings = alphabeticCharacters
  .concat(editActivationSpecialCharacters)
  .map((char) => [char, '_handleStartEditingKeyPress']);

/**
 * Bindings for "desktop" clients
 */
const desktopBindings = [
  ['enter', '_handleEnterKeyPress'],
  ['esc', '_handleEscKeyPress'],
  ['backspace', '_handleDeleteKeyPress'],
  ['delete', '_handleDeleteKeyPress'],
  ['esc', '_handleEscKeyPress_CellEditing', KeyScopes.CellEditing],
]
  .concat(editActivationBindings)
  .concat(editModeBindings);
/**
 * Some bindings only apply to "desktop" clients
 */
if (!isMobile()) {
  bindings = bindings.concat(desktopBindings);
}

/**
 * END - key setup bindings
 */

/**
 * HotKeyMixin - setup key bindings for the cuesheet
 */
export default {
  componentDidMount() {
    this._bindHotKeys();

    this.listenTo(KeyBindingActions.bindHotKeys, this.onBindHotKeys);
    this.listenTo(KeyBindingActions.unbindHotKeys, this.onUnbindHotKeys);
    this.listenTo(OnlineIndicatorActions.setConnected, this.onBindHotKeys);
    this.listenTo(OnlineIndicatorActions.setDisconnected, this.onUnbindHotKeys);
  },

  componentDidUpdate(prevProps, prevState) {
    /**
     * We do this to prevent hot keys from being triggered when popovers are visible
     */
    prevState = prevState || {};
    const state = this.state || {};
    const popoverPrevVisible = prevState.popoverVisible;
    const popoverCurrVisible = state.popoverVisible;

    if (
      (!popoverCurrVisible && !popoverPrevVisible) ||
      (popoverCurrVisible && popoverPrevVisible)
    )
      return;

    if (!popoverCurrVisible && popoverPrevVisible) {
      this.onBindHotKeys();
    } else {
      this.onUnbindHotKeys();
    }
  },

  componentWillUnmount() {
    this._unbindHotKeys();
  },

  onBindHotKeys() {
    this._bindHotKeys();
  },

  onUnbindHotKeys() {
    this._unbindHotKeys();
  },

  _hasTeamAdminRole() {
    let state = this.state;

    if (!state.user || !state.cuesheet) return;

    return role.hasTeamAdminRole(state.user, state.cuesheet.get('teamId'));
  },

  _hasEventAdminRole() {
    const state = this.state;

    if (!state.user || !state.cuesheet) return;

    return role.hasEventAdminRole(
      state.user,
      state.cuesheet.get('teamId'),
      state.cuesheet.get('eventId'),
    );
  },

  _hasEventContributorRole() {
    const state = this.state;

    if (!state.user || !state.cuesheet) return;

    return role.hasEventContributorRole(
      state.user,
      state.cuesheet.get('teamId'),
      state.cuesheet.get('eventId'),
    );
  },

  _inRowSelectionMode() {
    const state = this.state;

    if (!state.user || !state.cuesheet) return;

    return state.cuesheet.get('rowSelectionMode');
  },

  _toggleSearchVisibilityKeyPress(e) {
    e.preventDefault();
    SearchActions.toggleVisibility();
  },

  _handleClearFormatCellKeyPress(e) {
    e.preventDefault();
    CuesheetCellActions.saveSelectedCellFormat(null);
  },

  _handleCopyCellsKeyPress(e) {
    e.preventDefault();
    CuesheetCellActions.addSelectedCellsToPasteBuffer();
  },

  _handleCutCellsKeyPress(e) {
    e.preventDefault();
    CuesheetCellActions.addSelectedCellsToCutBuffer();
  },

  _handlePasteCellsKeyPress(e) {
    e.preventDefault();
    CuesheetCellActions.pasteCellsBuffer();
  },

  _handleToggleRowLockKeyPress(e) {
    e.preventDefault();
    if (this._hasEventAdminRole()) CuesheetRowActions.toggleRowLock();
  },

  _handleToggleRowSelectionKeyPress(e) {
    e.preventDefault();
    if (this._hasEventAdminRole())
      CuesheetRowActions.toggleMultipleRowSelection();
  },

  _handleDeleteRowKeyPress(e) {
    e.preventDefault();

    if (this._hasEventAdminRole()) {
      if (this._inRowSelectionMode()) {
        CuesheetRowActions.deleteSelectedRowsWithConfirmation();
      } else {
        CuesheetRowActions.handleDeleteRowWithConfirmation();
      }
    }
  },

  _handleTimeStampKeyPress(e) {
    e.preventDefault();
    if (this._hasEventContributorRole())
      CuesheetCellActions.saveSelectedCellTextWithTimeStamp();
  },

  _handleMakeColumnGrowKeyPress(e) {
    e.preventDefault();
    if (this._hasEventAdminRole())
      CuesheetColumnActions.updateColumnWidthGrow();
  },

  _handleMakeColumnShrinkKeyPress(e) {
    e.preventDefault();
    if (this._hasEventAdminRole())
      CuesheetColumnActions.updateColumnWidthShrink();
  },

  _handleDuplicateRowKeyPress(e) {
    e.preventDefault();
    if (this._hasEventAdminRole()) CuesheetRowActions.duplicateItemRow();
  },

  _handleConvertToTitleRowKeyPress(e) {
    e.preventDefault();
    if (this._hasEventAdminRole()) CuesheetRowActions.toggleTitleRow();
  },

  _handleAddRowAboveKeyPress(e) {
    e.preventDefault();

    if (this._hasEventAdminRole()) CuesheetRowActions.addItemRowAbove();
  },

  _handleAddRowBelowKeyPress(e) {
    e.preventDefault();

    if (this._hasEventAdminRole()) CuesheetRowActions.addItemRowBelow();
  },

  _handleAddTitleRowAboveKeyPress(e) {
    e.preventDefault();

    if (this._hasEventAdminRole()) CuesheetRowActions.addTitleRowAbove();
  },

  _handleAddTitleRowBelowKeyPress(e) {
    e.preventDefault();

    if (this._hasEventAdminRole()) CuesheetRowActions.addTitleRowBelow();
  },

  _handleMoveRowAboveKeyPress(e) {
    e.preventDefault();

    if (this._hasEventAdminRole()) CuesheetRowActions.moveItemRowUp();
  },

  _handleMoveRowBelowKeyPress(e) {
    e.preventDefault();

    if (this._hasEventAdminRole()) CuesheetRowActions.moveItemRowDown();
  },

  _handleCopyRowKeyPress(e) {
    e.preventDefault();

    if (this._hasEventAdminRole()) {
      if (this._inRowSelectionMode()) {
        CuesheetRowActions.addSelectionToPasteBuffer();
      } else {
        CuesheetRowActions.addRowToPasteBuffer();
      }
    }
  },

  _handleCutRowKeyPress(e) {
    e.preventDefault();

    if (this._hasEventAdminRole()) {
      if (this._inRowSelectionMode()) {
        CuesheetRowActions.addSelectionToCutBuffer();
      } else {
        CuesheetRowActions.addRowToCutBuffer();
      }
    }
  },

  _handlePasteRowBelowKeyPress(e) {
    e.preventDefault();

    if (this._hasEventAdminRole()) {
      if (this._inRowSelectionMode()) {
        ConfirmActions.prompt(MessageConstants.bulkWarning, () =>
          CuesheetRowActions.pasteBufferBelow(),
        );
      } else {
        CuesheetRowActions.pasteBufferBelow();
      }
    }
  },

  _handleToggleRowHighlightKeyPress(e) {
    e.preventDefault();

    if (this._hasEventAdminRole()) CuesheetRowActions.toggleRowHighlight();
  },

  _handleToggleRowPersonalHighlightKeyPress(e) {
    e.preventDefault();
    CuesheetRowActions.toggleRowPersonalHighlight();
  },

  _handleMoveColumnRightKeyPress(e) {
    e.preventDefault();

    if (isGlobalElementManager()) return;

    if (this._getDisplayType() !== DisplayTypes.show) {
      CuesheetColumnActions.moveColumnRight();
    } else {
      CuesheetColumnActions.moveUserColumnRight();
    }
  },

  _handleMoveColumnLeftKeyPress(e) {
    e.preventDefault();

    if (isGlobalElementManager()) return;

    if (this._getDisplayType() !== DisplayTypes.show) {
      CuesheetColumnActions.moveColumnLeft();
    } else {
      CuesheetColumnActions.moveUserColumnLeft();
    }
  },

  _getDisplayType() {
    return (
      this.state.cuesheet.getIn(['show', 'displayType']) || DisplayTypes.show
    );
  },

  _handleHideColumnKeyPress(e) {
    e.preventDefault();

    if (isGlobalElementManager()) return;

    const state = this.state.cuesheet;

    if (state.get('selectedCells').size < 1) return -1;
    const cellType = state.get('selectedCells').first().cellSpec.get('type');

    if (cellType === 'Note') {
      CuesheetColumnActions.hideNoteColumn();
    } else {
      CuesheetColumnActions.hideColumn();
    }
  },

  _handleShowAllColumnKeyPress(e) {
    e.preventDefault();

    if (isGlobalElementManager()) return;

    CuesheetColumnActions.showColumns();
  },

  _handleToggleActivityFeedKeyPress(e) {
    e.preventDefault();
    ActivityAuditListActions.toggleVisibility();
  },

  _handleBoldTextKeyPress(e) {
    e.preventDefault();
    CuesheetToolbarActions.toggleBold();
  },

  _handleItalicTextKeyPress(e) {
    e.preventDefault();
    CuesheetToolbarActions.toggleItalic();
  },

  _handleCellTextColorKeyPress(e) {
    e.preventDefault();
    CuesheetCellActions.toggleCellForeColor();
  },

  _handleCellBgColorKeyPress(e) {
    e.preventDefault();
    CuesheetCellActions.toggleCellBackColor();
  },

  _handleCustomCellFormatOneKeyPress(e) {
    e.preventDefault();
    if (isFeatured()) return;
    this._setCustomCellColorsFormat('red0', 'black1');
  },

  _handleCustomCellFormatTwoKeyPress(e) {
    e.preventDefault();
    if (isFeatured()) return;
    this._setCustomCellColorsFormat('initial', 'red0');
  },

  _handleCustomCellFormatThreeKeyPress(e) {
    e.preventDefault();
    if (isFeatured()) return;
    this._setCustomCellColorsFormat('green0', 'black0');
  },

  _handleCustomCellFormatFourKeyPress(e) {
    e.preventDefault();
    if (isFeatured()) return;
    this._setCustomCellColorsFormat('yellow0', 'black0');
  },

  _handleCustomCellFormatFiveKeyPress(e) {
    e.preventDefault();
  },

  _handleCustomCellFormatSixKeyPress(e) {
    e.preventDefault();
  },

  _handleCustomCellFormatSevenKeyPress(e) {
    e.preventDefault();
  },

  _handleCustomCellFormatEightKeyPress(e) {
    e.preventDefault();
  },

  _handleCustomCellFormatNineKeyPress(e) {
    e.preventDefault();
  },

  _handleCustomCellFormatZeroKeyPress(e) {
    e.preventDefault();
  },

  _handleCustomInlineStyleOneKeyPress(e) {
    e.preventDefault();
    RTEActions.applyInlineFormatting(['color-black0', 'bgcolor-white']);
  },

  _handleCustomInlineStyleTwoKeyPress(e) {
    e.preventDefault();
    RTEActions.applyInlineFormatting(['color-red0', 'bgcolor-white']);
  },

  _handleCustomInlineStyleThreeKeyPress(e) {
    e.preventDefault();
    RTEActions.applyInlineFormatting(['color-red0']);
  },

  _handleCustomInlineStyleFourKeyPress(e) {
    e.preventDefault();
    RTEActions.applyInlineFormatting(['color-green0', 'bgcolor-white']);
  },

  _handleCustomInlineStyleFiveKeyPress(e) {
    e.preventDefault();
    RTEActions.applyInlineFormatting(['color-green0']);
  },

  _handleCustomInlineStyleSixKeyPress(e) {
    e.preventDefault();
    RTEActions.applyInlineFormatting(['color-yellow0', 'bgcolor-white']);
  },

  _handleCustomInlineStyleSevenKeyPress(e) {
    e.preventDefault();
    RTEActions.applyInlineFormatting(['color-yellow0']);
  },

  _handleCustomInlineStyleEightKeyPress(e) {
    e.preventDefault();
    RTEActions.applyInlineFormatting(['color-blue0', 'bgcolor-white']);
  },

  _handleCustomInlineStyleNineKeyPress(e) {
    e.preventDefault();
    RTEActions.applyInlineFormatting(['color-blue0']);
  },

  _setCustomCellColorsFormat(backColor, foreColor) {
    let format = Map();
    if (backColor) format = format.set('backColor', backColor);
    if (foreColor) format = format.set('foreColor', foreColor);

    CuesheetCellActions.saveSelectedCellFormat(format);
  },

  _handleCellTextGrowKeyPress(e) {
    e.preventDefault();
    CuesheetCellActions.toggleTextSizeGrow();
  },

  _handleCellTextShrinkKeyPress(e) {
    e.preventDefault();
    CuesheetCellActions.toggleTextSizeShrink();
  },

  _handleDeleteKeyPress(e) {
    e.preventDefault();

    CuesheetCellActions.clearSelectedCell();
  },

  _handleStartEditingKeyPress(e) {
    CuesheetCellActions.startEditingSelectedCell(e);
  },

  _handleEnterKeyPress(e) {
    e.preventDefault();

    CuesheetCellActions.startEditingSelectedCell();
  },

  _handleEscKeyPress(e) {
    e.preventDefault();

    CuesheetRowActions.clearRowSelection();
    CuesheetCellActions.clearPastableCellsBuffer();
    CuesheetCursorActions.selectLastCellSelected();
  },

  _handleSpaceKeyPress(e) {
    e.preventDefault();

    CuesheetRowActions.trackNextRow();
  },

  _handleCtrlSpaceKeyPress(e) {
    e.preventDefault();

    CuesheetRowActions.trackNextRow({ updateDuration: true });
  },

  _handleShiftSpaceKeyPress(e) {
    e.preventDefault();

    CuesheetRowActions.trackPreviousRow();
  },

  _handleTrackSelectedKeyPress(e) {
    e.preventDefault();

    CuesheetRowActions.trackSelectedRow();
  },

  _handlePublishGlobalElementChangesEverywhereKeyPress() {
    if (!this._hasTeamAdminRole() || this._inRowSelectionMode()) return;

    ModalActions.show(UIRegions.PublishGEChanges);
    CuesheetRowActions.publishSelectedShowRowChangesEverywhere();
  },

  _handleInsertGlobalElementsKeyPress() {
    if (!this._hasTeamAdminRole()) return;
    ModalActions.show(UIRegions.InsertGlobalElementsModal);
  },

  _handleCreateGlobalElementKeyPress() {
    if (!this._hasTeamAdminRole()) return;

    if (this._inRowSelectionMode()) {
      NewGlobalElementModalActions.populateSelection();
    } else {
      NewGlobalElementModalActions.populate();
    }
  },

  _handleRetrieveLatestGlobalElementVersionKeyPress() {
    if (!this._hasTeamAdminRole() || this._inRowSelectionMode()) return;

    const { teamId, eventId, showId } = this.context.router.state.params;

    ConfirmActions.prompt(
      MessageConstants.retrieveLatestGlobalElementChanges,
      () =>
        CuesheetRowActions.retrieveLatestGlobalElementVersionForSelectedRow(
          teamId,
          eventId,
          showId,
        ),
    );
  },

  _handleSaveCurrentGlobalElementKeyPress() {
    if (!this._hasTeamAdminRole() || this._inRowSelectionMode()) return;

    const { teamId, eventId, showId } = this.context.router.state.params;

    ConfirmActions.prompt(
      MessageConstants.publishShowRowChangesToGlobalElementManager,
      () =>
        CuesheetRowActions.publishSelectedShowRowChangesToGlobalElementRow(
          teamId,
          eventId,
          showId,
        ),
    );
  },

  _handleEscKeyPress_CellEditing(e) {
    e.preventDefault();

    CuesheetCellActions.revertToLastSuccessfulCellState();
    CuesheetTransientCellActions.clearTransientCellValue();
    CuesheetCursorActions.clearEditing();
  },

  _handleSaveShowKeyPress(e) {
    e.preventDefault();

    if (!this.state || !this.state.cuesheet) return;
    const state = this.state.cuesheet;

    if (this._hasEventAdminRole()) {
      ConfirmActions.prompt(
        'This will save a timestamped copy of your show. You can find it on your shows dashboard.',
        () => {
          EventShowListActions.createSavedVersion(
            state.get('teamId'),
            state.get('eventId'),
            state.get('showId'),
            state.getIn(['show', 'displayType']),
          );
        },
      );
    }
  },

  _handleShowUndoForCellPress() {
    CuesheetCellActions.showSelectedCellHistory();
  },

  _handleDisplayKeyboardShortcutsModalKeyPress(e) {
    e.preventDefault();
    ModalActions.show(UIRegions.KeyboardShortcuts);
  },

  _handleDisplayAdvancedPdfModalKeyPress(e) {
    e.preventDefault();
    ModalActions.show(UIRegions.ExportForm);
  },

  _handleSelectAllRows(e) {
    e.preventDefault();
    CuesheetRowActions.selectAllRows();
  },

  _bindHotKeys() {
    if (keysBound === true) return;

    keysBound = true;

    bindings.forEach((binding) => {
      const [key, handler, scope] = binding;
      keyBinder.assign(key, scope || KeyScopes.Default, (e) => {
        this[handler](e);
      });
    });
  },

  _unbindHotKeys() {
    if (keysBound === false) return;

    keysBound = false;

    keyBinder.unbind('enter', KeyScopes.Default, this._handleEnterKeyPress);

    bindings.forEach((binding) => {
      // eslint-disable-next-line no-unused-vars
      const [key, handler, scope] = binding;
      keyBinder.unbind(key, scope || KeyScopes.Default);
    });
  },
};
